import React, { useState, useEffect } from 'react';
import { providersAPI } from '../../api/providers';
import { authAPI } from '../../api/auth';
import { useAuth } from '../../hooks/useAuth';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import {
  Cog6ToothIcon,
  TruckIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  BellIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ProviderSettings = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [provider, setProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');

  const [settings, setSettings] = useState({
    autoAcceptBookings: false,
    bufferTimeMinutes: 15,
    genderPreference: 'any',
    offersMobile: false,
    maxTravelKm: '',
    emailNotifications: true,
    smsNotifications: true,
    bookingReminders: true,
    marketingEmails: false,
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    fetchSettings();
    try {
      const saved = JSON.parse(localStorage.getItem('providerNotificationPrefs') || '{}');
      setSettings(s => ({ ...s, ...saved }));
    } catch { /* ignore */ }
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await providersAPI.getMyProfile();
      setProvider(data);
      setSettings(s => ({
        ...s,
        autoAcceptBookings: data.autoAcceptBookings || false,
        bufferTimeMinutes: data.bufferTimeMinutes || 15,
        genderPreference: data.genderPreference || 'any',
        offersMobile: data.offersMobile || false,
        maxTravelKm: data.maxTravelKm || '',
      }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        autoAcceptBookings: settings.autoAcceptBookings,
        bufferTimeMinutes: parseInt(settings.bufferTimeMinutes) || 15,
        genderPreference: settings.genderPreference,
        offersMobile: settings.offersMobile,
      };
      if (settings.offersMobile && settings.maxTravelKm) {
        payload.maxTravelKm = parseFloat(settings.maxTravelKm);
      }
      await providersAPI.updateMyProfile(payload);

      localStorage.setItem('providerNotificationPrefs', JSON.stringify({
        emailNotifications: settings.emailNotifications,
        smsNotifications: settings.smsNotifications,
        bookingReminders: settings.bookingReminders,
        marketingEmails: settings.marketingEmails,
      }));

      toast.success('Settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    const nextErrors = {};
    if (!passwordForm.currentPassword) nextErrors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) nextErrors.newPassword = 'New password is required';
    else if (passwordForm.newPassword.length < 8) {
      nextErrors.newPassword = 'New password must be at least 8 characters';
    }
    if (!passwordForm.confirmPassword) nextErrors.confirmPassword = 'Please confirm your new password';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.resetPassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordErrors({ form: error?.response?.data?.message || 'Failed to update password' });
    } finally {
      setSavingPassword(false);
    }
  };

  const updatePasswordField = (field, value) => {
    setPasswordForm(p => ({ ...p, [field]: value }));
    setPasswordErrors(p => ({ ...p, [field]: '', form: '' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  const toggleStyle = (checked) => ({
    position: 'relative',
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: checked ? 'var(--orange-9)' : 'var(--gray-6)',
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.2s ease',
    flexShrink: 0,
  });

  const toggleDot = (checked) => ({
    position: 'absolute',
    top: '3px',
    left: checked ? '22px' : '3px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s ease',
  });

  const SettingRow = ({ title, desc, children, last = false }) => (
    <div
      className="flex items-center justify-between"
      style={{ padding: '14px 0', borderBottom: last ? 'none' : '1px solid var(--gray-4)' }}
    >
      <div style={{ maxWidth: '70%' }}>
        <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>{title}</p>
        <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '2px 0 0 0', textAlign: 'left' }}>{desc}</p>
      </div>
      {children}
    </div>
  );

  const tabs = [
    { id: 'bookings', label: 'Bookings', icon: Cog6ToothIcon },
    { id: 'mobile', label: 'Mobile Service', icon: TruckIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'account', label: 'Account', icon: UserCircleIcon },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px' }}>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Settings</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Manage your booking preferences, notifications, and account
          </p>
        </div>

        {/* Verification badge */}
        {provider && (
          <div
            className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
            style={{
              backgroundColor: provider.isApproved ? 'var(--green-2, #f0fdf4)' : 'var(--orange-2)',
              border: `1px solid ${provider.isApproved ? 'var(--green-6, #bbf7d0)' : 'var(--orange-5)'}`,
            }}
          >
            <ShieldCheckIcon style={{ width: '20px', height: '20px', color: provider.isApproved ? '#22c55e' : 'var(--orange-9)' }} />
            <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
              {provider.isApproved ? 'Verified Provider' : `Status: ${provider.verificationStatus}`}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div>
              <SettingRow
                title="Auto-accept bookings"
                desc="Automatically confirm new booking requests without manual approval"
              >
                <button
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, autoAcceptBookings: !s.autoAcceptBookings }))}
                  style={toggleStyle(settings.autoAcceptBookings)}
                >
                  <div style={toggleDot(settings.autoAcceptBookings)} />
                </button>
              </SettingRow>

              <SettingRow
                title="Buffer time between appointments"
                desc="Time gap between consecutive bookings for preparation"
              >
                <select
                  value={settings.bufferTimeMinutes}
                  onChange={(e) => setSettings(s => ({ ...s, bufferTimeMinutes: e.target.value }))}
                  style={{
                    padding: '6px 12px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  {[0, 5, 10, 15, 20, 30, 45, 60].map(m => (
                    <option key={m} value={m}>{m === 0 ? 'None' : `${m} min`}</option>
                  ))}
                </select>
              </SettingRow>

              <SettingRow
                title="Gender preference"
                desc="Choose which clients you serve"
                last
              >
                <select
                  value={settings.genderPreference}
                  onChange={(e) => setSettings(s => ({ ...s, genderPreference: e.target.value }))}
                  style={{
                    padding: '6px 12px', borderRadius: '8px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="any">All Genders</option>
                  <option value="male_only">Male Clients Only</option>
                  <option value="female_only">Female Clients Only</option>
                </select>
              </SettingRow>
            </div>
          )}

          {/* Mobile Service Tab */}
          {activeTab === 'mobile' && (
            <div>
              <SettingRow
                title="Offer mobile services"
                desc="Travel to the client's location to provide your services"
                last={!settings.offersMobile}
              >
                <button
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, offersMobile: !s.offersMobile }))}
                  style={toggleStyle(settings.offersMobile)}
                >
                  <div style={toggleDot(settings.offersMobile)} />
                </button>
              </SettingRow>

              {settings.offersMobile && (
                <SettingRow
                  title="Maximum travel distance"
                  desc="How far you're willing to travel from your base location"
                  last
                >
                  <select
                    value={settings.maxTravelKm}
                    onChange={(e) => setSettings(s => ({ ...s, maxTravelKm: e.target.value }))}
                    style={{
                      padding: '6px 12px', borderRadius: '8px',
                      border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                    }}
                  >
                    <option value="">No limit</option>
                    {[5, 10, 15, 20, 25, 30, 50].map(km => (
                      <option key={km} value={km}>{km} km</option>
                    ))}
                  </select>
                </SettingRow>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div>
              <SettingRow
                title="Email notifications"
                desc="Receive booking updates and important alerts via email"
              >
                <button
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, emailNotifications: !s.emailNotifications }))}
                  style={toggleStyle(settings.emailNotifications)}
                >
                  <div style={toggleDot(settings.emailNotifications)} />
                </button>
              </SettingRow>

              <SettingRow
                title="SMS notifications"
                desc="Get text alerts for new bookings and cancellations"
              >
                <button
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, smsNotifications: !s.smsNotifications }))}
                  style={toggleStyle(settings.smsNotifications)}
                >
                  <div style={toggleDot(settings.smsNotifications)} />
                </button>
              </SettingRow>

              <SettingRow
                title="Booking reminders"
                desc="Reminders for upcoming appointments"
              >
                <button
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, bookingReminders: !s.bookingReminders }))}
                  style={toggleStyle(settings.bookingReminders)}
                >
                  <div style={toggleDot(settings.bookingReminders)} />
                </button>
              </SettingRow>

              <SettingRow
                title="Marketing emails"
                desc="Tips, news, and promotions from the platform"
                last
              >
                <button
                  type="button"
                  onClick={() => setSettings(s => ({ ...s, marketingEmails: !s.marketingEmails }))}
                  style={toggleStyle(settings.marketingEmails)}
                >
                  <div style={toggleDot(settings.marketingEmails)} />
                </button>
              </SettingRow>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-5">
              <div>
                <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px', textAlign: 'left' }}>
                  Change Password
                </p>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px', textAlign: 'left' }}>
                  Update your password to keep your account secure
                </p>

                <div className="space-y-4">
                  <Input
                    label="Current Password"
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={(e) => updatePasswordField('currentPassword', e.target.value)}
                    placeholder="Enter current password"
                    error={passwordErrors.currentPassword}
                    showPasswordToggle
                  />
                  <Input
                    label="New Password"
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={(e) => updatePasswordField('newPassword', e.target.value)}
                    placeholder="At least 8 characters"
                    error={passwordErrors.newPassword}
                    showPasswordToggle
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => updatePasswordField('confirmPassword', e.target.value)}
                    placeholder="Re-enter new password"
                    error={passwordErrors.confirmPassword}
                    showPasswordToggle
                  />
                  {passwordErrors.form && (
                    <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '13px', margin: 0, textAlign: 'left' }}>
                      {passwordErrors.form}
                    </p>
                  )}
                </div>

                <div className="flex justify-end mt-5">
                  <Button onClick={handlePasswordChange} loading={savingPassword}>
                    Update Password
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div>
              <SettingRow title="Email" desc={user?.email || '—'}>
                <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>Read-only</span>
              </SettingRow>

              <SettingRow
                title="Account Status"
                desc={provider?.isApproved ? 'Your account is verified' : `Status: ${provider?.verificationStatus || 'pending'}`}
              >
                <span
                  style={{
                    padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                    backgroundColor: provider?.isApproved ? 'var(--green-3, #dcfce7)' : 'var(--orange-3)',
                    color: provider?.isApproved ? 'var(--green-11, #166534)' : 'var(--orange-11)',
                  }}
                >
                  {provider?.isApproved ? 'Verified' : 'Pending'}
                </span>
              </SettingRow>

              <SettingRow
                title="Log out"
                desc="Sign out of your account on this device"
                last
              >
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to log out?')) logout();
                  }}
                >
                  Log Out
                </Button>
              </SettingRow>

              {/* Danger Zone */}
              <div
                className="rounded-xl p-5 mt-8"
                style={{ backgroundColor: 'var(--red-2, #fff5f5)', border: '1px solid var(--red-6, #fca5a5)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon style={{ width: '18px', height: '18px', color: 'var(--red-9, #e5484d)' }} />
                  <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--red-9, #e5484d)', margin: 0, textAlign: 'left' }}>
                    Danger Zone
                  </h3>
                </div>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 14px', textAlign: 'left' }}>
                  Irreversible actions — please proceed with caution
                </p>

                <div className="flex items-start justify-between gap-4">
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--red-9, #e5484d)', margin: 0, textAlign: 'left' }}>
                      Delete Account
                    </p>
                    <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '2px 0 0', textAlign: 'left' }}>
                      Permanently delete your provider account, services, bookings, and all associated data.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      const confirmed = window.confirm(
                        'WARNING: This will permanently delete your account and all your data including services, bookings, reviews, and portfolio photos.\n\nThis action CANNOT be undone.\n\nAre you sure you want to proceed?'
                      );
                      if (confirmed) {
                        const doubleConfirm = window.prompt('To confirm, type "DELETE" below:');
                        if (doubleConfirm === 'DELETE') {
                          toast.error('Account deletion is not yet available. Please contact support.');
                        } else if (doubleConfirm !== null) {
                          toast.error('Account deletion cancelled — text did not match');
                        }
                      }
                    }}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save button — only for non-security tabs (security has its own save) */}
        {activeTab !== 'security' && activeTab !== 'account' && (
          <div className="flex justify-end mt-6">
            <Button onClick={handleSave} loading={saving} size="large">
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderSettings;
